/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReduxState } from 'store';
import {
  AnswerSubfield,
  Field,
  FormState,
  Item,
} from 'store/states/forms/types';
import { formatTimestampDate } from 'utils/fieldNotebookUtils/formatters';
import { collapseLineBreaks } from 'utils/fieldNotebookUtils/collapseLineBreaks';
import { isStoredFileUrl } from 'utils/fieldNotebookUtils/imageAnswer';
import { resolveFilePreview } from 'utils/fieldNotebookUtils/uploadPreviewCache';
import openFullIcon from 'assets/icons/open_full.svg';
import { Input } from '../Input/Input';
import { InputDate } from '../InputDate/InputDate';
import { MapsAnswer } from '../MapsAnswer/MapsAnswer';
import { ModalOpener } from './styled';

interface HistoricInputSelectorProps {
  field: Field;
  answer?: string | null;
  formDate: number;
  openImageModal: (imageurl: string, title: string, subtitle: string) => void;
  openImageGalleryModal: (
    urls: string[],
    title: string,
    subtitle: string,
  ) => void;
  openThumbnailModal: (
    signature: string,
    title: string,
    subtitle: string,
  ) => void;
  openDrawModal: (draw: string, title: string, subtitle: string) => void;
}

function getLabelByValue(items?: Item[] | null, value?: string | null): string {
  return items?.find((item) => item.value === value)?.label ?? value ?? '';
}

function parseUrls(answer: string): string[] {
  const trimmed = answer.trim();
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) {
        return arr
          .filter((u): u is string => typeof u === 'string' && u.trim() !== '')
          .map((u) => u.trim());
      }
    } catch {
      /* fall through */
    }
  }
  return [trimmed];
}

function isPdfUrl(url: string): boolean {
  return url.includes('response-content-type=application%2Fpdf');
}

export function HistoricInputSelector({
  field,
  answer,
  formDate,
  openImageModal,
  openImageGalleryModal,
  openThumbnailModal,
  openDrawModal,
}: HistoricInputSelectorProps) {
  const { i18n, t } = useTranslation();
  const locale = i18n.language;

  const { selectedForm } = useSelector<ReduxState, FormState>(
    (state) => state.forms,
  );
  const formattedDate = formatTimestampDate(formDate, locale);

  const isEmpty = answer === '' || answer === null || answer === undefined;

  if (isEmpty) {
    return (
      <Input color="#767676" weight={400}>
        {t('not_informed')}
      </Input>
    );
  }

  switch (field.type) {
    case 'date': {
      if (typeof answer !== 'string') {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      return <InputDate date={answer} />;
    }

    case 'image': {
      let urls: string[] = [];
      if (typeof answer === 'string') {
        urls = parseUrls(answer);
      } else if (Array.isArray(answer)) {
        urls = (answer as unknown as unknown[])
          .filter((u): u is string => typeof u === 'string' && u.trim() !== '')
          .map((u) => u.trim());
      }
      if (urls.length === 0) {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      // Itens recém-enviados (signed_id) só têm miniatura no cache local da sessão.
      const tiles = urls.map((item) => ({ item, preview: resolveFilePreview(item) }));
      const gallerySources = tiles
        .filter(({ preview }) => preview.kind === 'image' && preview.src)
        .map(({ preview }) => preview.src as string);
      const visible = tiles.slice(0, 4);
      const extraCount = tiles.length - visible.length;
      const modalSubtitle = `${selectedForm.title}: ${formattedDate}`;
      const openGallery = () => {
        openImageGalleryModal(gallerySources, t('image_of_your_note'), modalSubtitle);
      };
      const count = visible.length;
      const gridCols = count === 1 ? '1fr' : '1fr 1fr';
      const gridRows = count <= 2 ? '1fr' : '1fr 1fr';
      const handleTileClick = (item: string, src?: string) => {
        if (isPdfUrl(item) || (isStoredFileUrl(item) && resolveFilePreview(item).kind === 'pdf')) {
          window.open(item, '_blank', 'noopener');
        } else if (!src) {
          /* arquivo sem miniatura disponível */
        } else if (gallerySources.length > 1) {
          openGallery();
        } else {
          openImageModal(src, t('image_of_your_note'), modalSubtitle);
        }
      };
      return (
        <div
          style={{
            display: 'grid',
            gap: 2,
            width: 180,
            height: 180,
            gridTemplateColumns: gridCols,
            gridTemplateRows: gridRows,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {visible.map(({ item, preview }, i) => {
            const showOverlay =
              i === visible.length - 1 && extraCount > 0;
            const spanStyle =
              count === 3 && i === 0
                ? { gridRow: 'span 2' as const }
                : {};
            const isPdf = preview.kind === 'pdf';
            const src = preview.kind === 'image' ? preview.src : undefined;
            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => handleTileClick(item, src)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTileClick(item, src);
                  }
                }}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                  ...(isPdf || !src
                    ? {
                        background: '#fff',
                        border: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : {
                        backgroundImage: `url(${src})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }),
                  ...spanStyle,
                }}
              >
                {isPdf && (
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: '#D32F2F',
                      letterSpacing: 1,
                    }}
                  >
                    .PDF
                  </span>
                )}
                {showOverlay && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      fontWeight: 600,
                    }}
                  >
                    +{extraCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    case 'geolocation': {
      if (typeof answer !== 'string') {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      const coordinates = answer.split(',');
      return (
        <MapsAnswer
          lat={coordinates[0]}
          long={coordinates[1]}
          subtitle={`${selectedForm.title}: ${formattedDate}`}
        />
      );
    }
    case 'signature': {
      if (typeof answer !== 'string') {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      return (
        <ModalOpener
          image={answer}
          onClick={() =>
            openThumbnailModal(
              answer,
              `${t('show_signature_modal_title')}`,
              formattedDate,
            )
          }
        >
          <div>
            <p>{t('tap_to_full_screen')}</p>
            <img src={openFullIcon} alt="open full icon" />
          </div>
        </ModalOpener>
      );
    }
    case 'draw': {
      if (typeof answer !== 'string') {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      return (
        <ModalOpener
          image={answer}
          onClick={() =>
            openDrawModal(
              answer,
              `${t('show_signature_modal_title')}`,
              formattedDate,
            )
          }
        >
          <div>
            <p>{t('tap_to_full_screen')}</p>
            <img src={openFullIcon} alt="open full icon" />
          </div>
        </ModalOpener>
      );
    }

    case 'checkbox_group': {
      let answerSubfield: AnswerSubfield[] = [];
      if (typeof answer === 'string') {
        try {
          const parsed = JSON.parse(answer);
          if (Array.isArray(parsed)) answerSubfield = parsed;
        } catch {
          answerSubfield = [];
        }
      } else if (Array.isArray(answer)) {
        answerSubfield = answer as AnswerSubfield[];
      }

      if (answerSubfield.length === 0) {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }

      return (
        <Grid container spacing={2} sx={{ pl: 1 }}>
          {answerSubfield.map((parent, index) => {
            const parentLabel = getLabelByValue(field.items, parent.value);
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <p
                  style={{
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: 15,
                    margin: 0,
                  }}
                >
                  {parentLabel}
                </p>
                {parent.sub_fields?.map((sub, i) => {
                  const subDef = field.sub_fields?.find(
                    (sf) => sf.field_id === sub.field_id,
                  );
                  const rawValue = sub.value;
                  const hasValue =
                    rawValue !== null &&
                    rawValue !== undefined &&
                    rawValue !== '';
                  const resolved = hasValue
                    ? subDef
                      ? getLabelByValue(subDef.items, String(rawValue))
                      : String(rawValue)
                    : '';
                  const displayValue =
                    collapseLineBreaks(resolved) || t('not_informed');
                  return (
                    <div key={i} style={{ marginTop: 8 }}>
                      <p
                        style={{
                          color: '#616161',
                          fontSize: 13,
                          fontWeight: 500,
                          margin: 0,
                          marginBottom: 4,
                        }}
                      >
                        {subDef?.summary_title ?? subDef?.description ?? ''}
                      </p>
                      <p
                        style={{
                          color: '#000',
                          fontWeight: 500,
                          fontSize: 15,
                          margin: 0,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {displayValue}
                      </p>
                    </div>
                  );
                })}
              </Grid>
            );
          })}
        </Grid>
      );
    }

    case 'checkbox': {
      if (typeof answer !== 'string') {
        return (
          <Input color="#767676" weight={400}>
            {t('not_informed')}
          </Input>
        );
      }
      const allItemsAnswereds = answer.split('\t').slice(1);
      const answerString = allItemsAnswereds
        .map(
          (itemAnswer) =>
            field.items.find((fieldItem) => fieldItem.value === itemAnswer)
              ?.label ?? itemAnswer,
        )
        .join(', ');
      return <Input>{answerString}</Input>;
    }
    case 'radio': {
      const item =
        field.items.find((fieldItem) => fieldItem.value === answer)?.label ??
        answer;
      return <Input>{item}</Input>;
    }

    default:
      return <Input>{collapseLineBreaks(answer)}</Input>;
  }
}
